import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import Utils from '../../../util';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { takeUntil } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { EarningsKeys } from '../../../models/results/financial/ispl/earnings.model';
import { LooseObject } from '../../../models/loose-object.model';
import { Expenses } from '../../../models/results/financial/ispl/ispl.model';
import { Revenues } from '../../../models/results/financial/ispl/revenues.model';
import { PlotlyTrace, YearData } from '../../../models/results/plotly.model';

export class DisplayData {
  public hasGraphData: boolean;
  public graphData?: PlotlyTrace[];
  public hasTableData: boolean;
  public tableData?: LooseObject[];
  public tableHeaders?: string[];

  constructor() {
    this.hasGraphData = false;
    this.hasTableData = false;
  }

  setGraphData(graphData: any[]) {
    this.hasGraphData = true;
    this.graphData = graphData;
  }

  setTableData(tableData: any[], tableHeaders: string[]) {
    this.hasTableData = true;
    this.tableData = tableData;
    this.tableHeaders = tableHeaders;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FinancialGeneralService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();

  constructor(
    private projectCommService: ProjectCommunicationService,
    private translationService: AnessaTranslationService,
    private unitService: UnitService
  ) {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {});
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  getOpexBreakdown(selectedCurrency: Unit, truncateNameForGraph = false) {
    const displayData = new DisplayData();
    const precision = this.projectCommService.getPrecision();
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);

    const opexExpenses = {
      'RESULTS.FINANCIAL_GENERAL.FEEDSTOCK_EXPENSES': 'RESULTS.DATA.ISPL.FE',
      'RESULTS.FINANCIAL_GENERAL.DIGESTATE_EXPENSES': 'RESULTS.DATA.ISPL.DE',
      'RESULTS.FINANCIAL_GENERAL.CONSUMED_FUELS': 'RESULTS.DATA.ISPL.UCF',
      'RESULTS.FINANCIAL_GENERAL.LABOUR': 'RESULTS.DATA.ISPL.LABOUR',
      'RESULTS.FINANCIAL_GENERAL.MAINTENANCE': 'RESULTS.DATA.ISPL.MAINTENANCE',
      'RESULTS.FINANCIAL_GENERAL.MAJOR_MAINTENANCE': 'RESULTS.DATA.ISPL.PMM',
      'RESULTS.FINANCIAL_GENERAL.CONSUMABLES': 'RESULTS.DATA.ISPL.CONSUMABLES',
      'RESULTS.FINANCIAL_GENERAL.INSURANCE': 'RESULTS.DATA.INSURANCE',
      'RESULTS.FINANCIAL_GENERAL.OTHER': 'RESULTS.DATA.ISPL.OE'
    };

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const averageCostHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE_COST') +
      ' (' + currencyTranslation + '/' + this.translationService.get('RESULTS.GENERAL.YEAR') + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, averageCostHeader, shareHeader];

    const fixedOpex = [
      'RESULTS.FINANCIAL_GENERAL.FEEDSTOCK_EXPENSES',
      'RESULTS.FINANCIAL_GENERAL.DIGESTATE_EXPENSES',
      'RESULTS.FINANCIAL_GENERAL.CONSUMED_FUELS'
    ];
    const plStatement = this.projectCommService.getResults()?.financial.ispl;
    if (!plStatement) return;

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalOpex = 0;
    for (const opexName in opexExpenses) {
      const opexKey = opexName as keyof typeof opexExpenses;
      const key = opexExpenses[opexKey] as keyof Expenses;
      const nameTranslation = this.translationService.get(opexName);
      let expenseAverage = 0;

      const subTable = plStatement.expenses[key];
      if (!subTable || Object.keys(subTable).length < 1) continue;

      for (const result in subTable) {
        const resKey = result as keyof typeof subTable;
        const data: ResultRow = subTable[resKey];
        let total = 0;
        for (let i = 1; i < data.values.length; i++) {
          total += Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
        }
        const average = total / (data.values.length - 1) * -1;
        expenseAverage += average;
        if (!fixedOpex.includes(opexName)) {
          const name = truncateNameForGraph ? Utils.truncateString(result, 30) : result;
          graphData.push({
            name: name,
            value: Utils.roundToPrecision(average, precision),
            unit: currencyTranslation
          });
          const tableDataItem: LooseObject = {};
          tableDataItem[nameHeader] = result;
          tableDataItem[averageCostHeader] = Utils.roundToPrecision(average, precision);
          tableDataItem[shareHeader] = null;
          tableData.push(tableDataItem);
        }
      }
      totalOpex += expenseAverage;
      if (fixedOpex.includes(opexName)) {
        const name = truncateNameForGraph ? Utils.truncateString(nameTranslation, 30) : nameTranslation;
        graphData.push({
          name: name,
          value: Utils.roundToPrecision(expenseAverage, precision),
          unit: currencyTranslation
        });
        const tableDataItem: LooseObject = {};
        tableDataItem[nameHeader] = nameTranslation;
        tableDataItem[averageCostHeader] = Utils.roundToPrecision(expenseAverage, precision);
        tableDataItem[shareHeader] = null;
        tableData.push(tableDataItem);
      }
    }

    for (const data of tableData) {
      if (totalOpex === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalOpex * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.DATA.TOTAL_OPEX');
    totalItem[averageCostHeader] = Utils.roundToPrecision(totalOpex, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getCapexBreakdown(selectedCurrency: Unit, truncateNameForGraph = false): DisplayData {
    const displayData = new DisplayData();
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);
    const precision = this.projectCommService.getPrecision();
    const project = this.projectCommService.getResultProjectState();
    if (!project) return displayData;
    const capex = project.financial.capex;

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const costHeader = this.translationService.get('RESULTS.GENERAL.COST') + ' (' + currencyTranslation + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, costHeader, shareHeader];

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalCapex = 0;
    for (const expense of capex) {
      let convertedValue;
      if (expense.connectionToggle) {
        const rate = this.projectCommService.getResults()?.technical.product_rates[expense.connectionType];
        if (!rate || !expense.cost.value) continue;
        const periodInDays = this.unitService.getValueInBaseUnit(expense.period);
        const amount = this.unitService.getValueInBaseUnit(expense.quantity);
        let cost = expense.cost.value * (rate * periodInDays / amount);
        convertedValue = Unit.convertValue(cost, expense.cost.unit, selectedCurrency.id);
      } else {
        convertedValue = Unit.convertValue(expense.cost.value, expense.cost.unit, selectedCurrency.id);
      }
      totalCapex += convertedValue;
      const name = truncateNameForGraph ? Utils.truncateString(expense.name, 30) : expense.name;

      graphData.push({
        name: name,
        value: Utils.roundToPrecision(convertedValue, precision),
        unit: currencyTranslation
      });
      const tableDataItem: LooseObject = {};
      tableDataItem[nameHeader] = expense.name;
      tableDataItem[costHeader] = Utils.roundToPrecision(convertedValue, precision);
      tableDataItem[shareHeader] = null;
      tableData.push(tableDataItem);
    }

    for (const data of tableData) {
      if (totalCapex === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalCapex * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.DATA.TOTAL_CAPEX');
    totalItem[tableHeaders[1]] = Utils.roundToPrecision(totalCapex, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getRevenuesBreakdown(selectedCurrency: Unit, truncateNameForGraph = false): DisplayData {
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);
    const displayData = new DisplayData();
    const precision = this.projectCommService.getPrecision();
    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE_REVENUE') + ' ('
      + currencyTranslation + '/' + this.translationService.get('RESULTS.GENERAL.YEAR') + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, averageHeader, shareHeader];

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalRevenue = 0;
    for (const type in plStatement?.revenues) {
      const key = type as keyof Revenues;
      const translation = this.translationService.get(type);
      let average = 0;
      const subTable = plStatement.revenues[key];
      if (!subTable) continue;
      for (const income in subTable) {
        const data = subTable[income];
        // Detect variable pricing structure and adjust accordingly
        if (!('RESULTS.DATA.VALUES' in data)) {
          if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
          for (const breakdown in data) {
            let total = 0;
            for (let i = 1; i < data[breakdown].values.length; i++) {
              total += Unit.convertValue(data[breakdown].values[i], data[breakdown].unit, selectedCurrency.id);
            }
            average += total / (data[breakdown].values.length - 1);
          }
        } else {
          let total = 0;
          for (let i = 1; i < data.values.length; i++) {
            total += Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
          }
          average += total / (data.values.length - 1);
        }
      }
      totalRevenue += average;
      const name = truncateNameForGraph ? Utils.truncateString(translation, 30) : translation;

      graphData.push({
        name: name,
        value: Utils.roundToPrecision(average, precision),
        unit: currencyTranslation
      });
      const tableDataItem: LooseObject = {};
      tableDataItem[nameHeader] = translation;
      tableDataItem[averageHeader] = Utils.roundToPrecision(average, precision);
      tableDataItem[shareHeader] = null;
      tableData.push(tableDataItem);
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.GENERAL.TOTAL');
    totalItem[averageHeader] = Utils.roundToPrecision(totalRevenue, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getSellingProductsRevenueBreakdown(selectedCurrency: Unit, truncateNameForGraph = false): DisplayData {
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);
    const displayData = new DisplayData();
    const precision = this.projectCommService.getPrecision();
    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE_REVENUE') + ' ('
      + currencyTranslation + '/' + this.translationService.get('RESULTS.GENERAL.YEAR') + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, averageHeader, shareHeader];

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalRevenue = 0;
    // no translation needed here, this is user entered
    for (const income in plStatement?.revenues.sp) {
      const data = plStatement.revenues.sp[income];
      // Detect variable pricing structure and adjust accordingly
      if (!(data instanceof ResultRow)) {
        if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
        for (const breakdown in data) {
          let total = 0;
          for (let i = 1; i < data[breakdown].values.length; i++) {
            total += data[breakdown].values[i];
          }
          const average = Unit.convertValue(total, data[breakdown].unit, selectedCurrency.id) / (data[breakdown].values.length - 1);
          totalRevenue += average;
          const labelTranslation = this.translationService.get(data[breakdown].label);
          const combinedName = `${income} - ${labelTranslation}`;
          const name = truncateNameForGraph ? Utils.truncateString(combinedName, 30) : combinedName;

          graphData.push({
            name: name,
            value: Utils.roundToPrecision(average, precision),
            unit: currencyTranslation
          });
          const tableDataItem: LooseObject = {};
          tableDataItem[nameHeader] = combinedName;
          tableDataItem[averageHeader] = Utils.roundToPrecision(average, precision);
          tableDataItem[shareHeader] = null;
          tableData.push(tableDataItem);
        }
      } else {
        let total = 0;
        for (let i = 1; i < data.values.length; i++) {
          total += data.values[i];
        }
        const average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
        totalRevenue += average;
        const name = truncateNameForGraph ? Utils.truncateString(income, 30) : income;

        graphData.push({
          name: name,
          value: Utils.roundToPrecision(average, precision),
          unit: currencyTranslation
        });
        const tableDataItem: LooseObject = {};
        tableDataItem[nameHeader] = income;
        tableDataItem[averageHeader] = Utils.roundToPrecision(average, precision);
        tableDataItem[shareHeader] = null;
        tableData.push(tableDataItem);
      }
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.GENERAL.TOTAL');
    totalItem[averageHeader] = Utils.roundToPrecision(totalRevenue, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getFeedstockRevenueBreakdown(selectedCurrency: Unit, truncateNameForGraph = false): DisplayData {
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);
    const displayData = new DisplayData();
    const precision = this.projectCommService.getPrecision();
    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE_REVENUE') + ' ('
      + currencyTranslation + '/' + this.translationService.get('RESULTS.GENERAL.YEAR') + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, averageHeader, shareHeader];

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalRevenue = 0;
    // no translation here, this is user entered
    for (const income in plStatement?.revenues.ftf) {
      const data = plStatement.revenues.ftf[income];
      let total = 0;
      for (let i = 1; i < data.values.length; i++) {
        total += data.values[i];
      }
      const average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
      totalRevenue += average;
      const name = truncateNameForGraph ? Utils.truncateString(income, 30) : income;

      graphData.push({
        name: name,
        value: Utils.roundToPrecision(average, precision),
        unit: currencyTranslation
      });
      const tableDataItem: LooseObject = {};
      tableDataItem[nameHeader] = income;
      tableDataItem[averageHeader] = Utils.roundToPrecision(average, precision);
      tableDataItem[shareHeader] = null;
      tableData.push(tableDataItem);
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.GENERAL.TOTAL');
    totalItem[averageHeader] = Utils.roundToPrecision(totalRevenue, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getAdditionalIncomeBreakdown(selectedCurrency: Unit, truncateNameForGraph = false): DisplayData {
    const currencyTranslation = this.translationService.get(selectedCurrency.translationId);
    const displayData = new DisplayData();
    const precision = this.projectCommService.getPrecision();
    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    const nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    const averageHeader = this.translationService.get('RESULTS.GENERAL.AVERAGE_REVENUE') + ' ('
      + currencyTranslation + '/' + this.translationService.get('RESULTS.GENERAL.YEAR') + ')';
    const shareHeader = this.translationService.get('RESULTS.GENERAL.SHARE');
    const tableHeaders = [nameHeader, averageHeader, shareHeader];

    const graphData: { name: string, value: number, unit: string }[] = [];
    const tableData: LooseObject[] = [];

    let totalRevenue = 0;
    // no translation here, this is user entered
    for (const income in plStatement?.revenues.ai) {
      const data = plStatement.revenues.ai[income];
      let total = 0;
      for (let i = 1; i < data.values.length; i++) {
        total += data.values[i];
      }
      const average = Unit.convertValue(total, data.unit, selectedCurrency.id) / (data.values.length - 1);
      totalRevenue += average;
      const name = truncateNameForGraph ? Utils.truncateString(income, 30) : income;

      graphData.push({
        name: name,
        value: Utils.roundToPrecision(average, precision),
        unit: currencyTranslation
      });
      const tableDataItem: LooseObject = {};
      tableDataItem[nameHeader] = income;
      tableDataItem[averageHeader] = Utils.roundToPrecision(average, precision);
      tableDataItem[shareHeader] = null;
      tableData.push(tableDataItem);
    }

    for (const data of tableData) {
      if (totalRevenue === 0) {
        data[shareHeader] = 'n/a';
      } else {
        data[shareHeader] = `${Utils.roundToPrecision(data[tableHeaders[1]] / totalRevenue * 100, this.projectCommService.getPrecision())}%`;
      }
    }
    const totalItem: LooseObject = {};
    totalItem[nameHeader] = this.translationService.get('RESULTS.GENERAL.TOTAL');
    totalItem[averageHeader] = Utils.roundToPrecision(totalRevenue, precision);
    totalItem[shareHeader] = '100%';
    tableData.push(totalItem);

    displayData.setGraphData(graphData);
    displayData.setTableData(tableData, tableHeaders);

    return displayData;
  }

  getRevenueTrend(selectedCurrency: Unit): DisplayData {
    const displayData = new DisplayData();

    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    const graphData: PlotlyTrace[] = [];
    for (const type in plStatement?.revenues) {
      const revenuesKey = type as keyof Revenues;
      const translation = this.translationService.get(type);
      const trace: PlotlyTrace = {x: [], y: [], name: translation, type: 'bar'};
      const subTable = plStatement.revenues[revenuesKey];
      if (!subTable) continue;
      for (const income in subTable) {
        const data = subTable[income];
        // Detect variable pricing structure and adjust accordingly
        if (!('RESULTS.DATA.VALUES' in data)) {
          if ('RESULTS.DATA.LABEL' in data || 'RESULTS.DATA.UNIT' in data) continue;
          for (const breakdown in data) {
            for (let i = 0; i < data[breakdown].values.length; i++) {
              if (!trace.x.includes(i)) {
                trace.x[i] = i;
                trace.y[i] = 0;
              }
              const value = Unit.convertValue(data[breakdown].values[i], data[breakdown].unit, selectedCurrency.id);
              trace.y[i] += Utils.roundToPrecision(value, this.projectCommService.getPrecision());
            }
          }
        } else {
          for (let i = 0; i < data.values.length; i++) {
            if (!trace.x.includes(i)) {
              trace.x[i] = i;
              trace.y[i] = 0;
            }
            const value = Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
            trace.y[i] += Utils.roundToPrecision(value, this.projectCommService.getPrecision());
          }
        }
      }
      graphData.push(trace);
    }

    displayData.setGraphData(graphData);

    return displayData;
  }

  getEarningsTrend(selectedCurrency: Unit): DisplayData {
    const displayData = new DisplayData();

    const plStatement = this.projectCommService.getResults()?.financial.ispl;

    let lifetime = 0;
    if (plStatement?.earnings) {
      for (const key in plStatement.earnings) {
        const typedKey = key as EarningsKeys;
        const subTable = plStatement.earnings[typedKey];
        const subKey = typedKey as keyof typeof subTable;
        const data: ResultRow = plStatement.earnings[typedKey][subKey];
        if (data.values.length > lifetime) {
          lifetime = data.values.length;
        }
      }
    }

    const graphData: YearData[] = [];
    for (let i = 0; i < lifetime; i++) {
      const yearData: YearData = {name: String(i), series: []};
      for (const key in plStatement?.earnings) {
        const typedKey = key as EarningsKeys;
        const subTable = plStatement.earnings[typedKey];
        const subKey = typedKey as keyof typeof subTable;
        const data: ResultRow = plStatement.earnings[typedKey][subKey];
        const value = Unit.convertValue(data.values[i], data.unit, selectedCurrency.id);
        const translation = this.translationService.get(data.label);
        yearData.series.push({
          name: translation,
          value: Utils.roundToPrecision(value, this.projectCommService.getPrecision())
        });
      }
      graphData.push(yearData);
    }

    displayData.setGraphData(graphData);

    return displayData;
  }
}


